
// Form4.jsx (no auto-save)
import { useState, useRef, useEffect, useCallback } from "react";
import styles from "./Form4.module.scss";
import saveIcon from "../../../assets/images/save-icon.svg";
import { Getcanleaddata, GetDepartments, PostRegister } from "../../../apiServices";

const EDUCATION_OPTIONS = ["Below 10", "10 Pass and Above", "Diploma", "ITI", "UG", "PG"];

const YEAR_OPTIONS = (() => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = currentYear; i >= 1970; i--) years.push(i.toString());
  return years;
})();

function AutoSelect({ options, placeholder, value, onChange, onBlur, isError, disabled, loading }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapRef = useRef(null);

  const getOptionString = (opt) => {
    if (typeof opt === 'string') return opt;
    if (typeof opt === 'object' && opt !== null) return opt.courses || opt.course_name || opt.name || opt.label || opt.value || String(opt);
    return String(opt);
  };

  const filtered = options.filter((opt) => getOptionString(opt).toLowerCase().includes(search.toLowerCase()));

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
        if (onBlur) onBlur();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onBlur]);

  return (
    <div className={`${styles.autoSelectWrap}${isError ? " " + styles.err : ""}${disabled ? " " + styles.disabled : ""}`} ref={wrapRef}>
      <div className={styles.autoSelectTrigger} onClick={() => !disabled && setOpen(p => !p)}>
        <span className={value ? styles.selectedVal : styles.placeholderVal}>{value || placeholder}</span>
        <span className={styles.arrow}>{open ? "▴" : "▾"}</span>
      </div>
      {open && (
        <div className={styles.dropdown}>
          <div className={styles.searchWrap}>
            <input autoFocus className={styles.searchInput} placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} onClick={(e) => e.stopPropagation()} />
          </div>
          <ul className={styles.optionList}>
            {loading ? (
              <li className={styles.noOption}>Loading...</li>
            ) : filtered.length > 0 ? (
              filtered.map((opt, i) => (
                <li key={i} className={`${styles.optionItem}${value === getOptionString(opt) ? " " + styles.active : ""}`}
                  onClick={() => { onChange(getOptionString(opt)); setOpen(false); setSearch(""); if (onBlur) onBlur(); }}>
                  {getOptionString(opt)}
                </li>
              ))
            ) : <li className={styles.noOption}>No options found</li>}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function Form4({ onClose, id, recordType, form3Data, onSubmit, onBack, registerValidation, onValidationChange }) {
  const [formData, setFormData] = useState({ education: "", specialization: "", completedYear: "" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [specializationOptions, setSpecializationOptions] = useState([]);
  const [loadingSpecialization, setLoadingSpecialization] = useState(false);
  const [touched, setTouched] = useState({});

  // --- FIX: stable refs ---
  const validateRef = useRef(null);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;

  const prevIsValidRef = useRef(true);

  const isLowEducation = formData.education === "Below 10" || formData.education === "10 Pass and Above";

  useEffect(() => { if (id) fetchEducationData(id); }, [id, recordType]);

  const fetchEducationData = async (recordId) => {
    setLoading(true);
    try {
      const response = await Getcanleaddata(recordId, recordType);
      if (response?.code === 200) {
        const candidate = response.data;
        const educationValue = candidate.qualification || "";
        setFormData({
          education: educationValue,
          specialization: candidate.specialization || "",
          completedYear: candidate.passed_out_year?.toString() || ""
        });
        setTouched({
          education: !!educationValue,
          specialization: !!candidate.specialization,
          completedYear: !!candidate.passed_out_year
        });
        if (educationValue && !["Below 10", "10 Pass and Above"].includes(educationValue)) {
          await fetchSpecializationForEducation(educationValue);
        }
      }
    } catch (error) { console.error("Error fetching education data:", error); }
    finally { setLoading(false); }
  };

  const fetchSpecializationForEducation = async (value) => {
    if (["Below 10", "10 Pass and Above"].includes(value)) { setSpecializationOptions([]); return; }
    const urlMap = { "Diploma": "diplomaCourses", "ITI": "ITICourses", "UG": "UGCourses", "PG": "PGCourses" };
    const end_url = urlMap[value];
    if (!end_url) return;

    setLoadingSpecialization(true);
    try {
      const response = await GetDepartments(end_url);
      if (response?.results) {
        setSpecializationOptions(response.results.map(item => item.courses || item.course_name || item.name || item));
      }
    } catch (error) { console.error("Failed to fetch specialization:", error); setSpecializationOptions([]); }
    finally { setLoadingSpecialization(false); }
  };

  // validate reads from ref - no deps
  const validate = useCallback((silent = false) => {
    const fd = formDataRef.current;
    const _isLow = fd.education === "Below 10" || fd.education === "10 Pass and Above";
    const isEducationValid = !!fd.education;

    if (_isLow) {
      if (!silent) setErrors(isEducationValid ? {} : { education: "Education is required" });
      return isEducationValid;
    }

    const isSpecializationValid = !!fd.specialization;
    const isYearValid = !!fd.completedYear;
    const isValid = isEducationValid && isSpecializationValid && isYearValid;

    if (!silent) {
      const errs = {};
      if (!isEducationValid) errs.education = "Education is required";
      if (!isSpecializationValid) errs.specialization = "Specialization is required";
      if (!isYearValid) errs.completedYear = "Degree completed year is required";
      setErrors(errs);
    }
    return isValid;
  }, []);

  validateRef.current = validate;

  // Register validation ONCE
  useEffect(() => {
    if (registerValidation) {
      registerValidation((silent = false) => validateRef.current(silent));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (onValidationChange) {
      const fd = formDataRef.current;
      const _isLow = fd.education === "Below 10" || fd.education === "10 Pass and Above";
      const isValid = _isLow ? !!fd.education : (!!fd.education && !!fd.specialization && !!fd.completedYear);
      if (prevIsValidRef.current !== isValid) {
        prevIsValidRef.current = isValid;
        onValidationChange();
      }
    }
  }, [formData.education, formData.specialization, formData.completedYear, onValidationChange]);

  const handleSelect = useCallback((field) => async (val) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    setFormData(prev => ({ ...prev, [field]: val }));
    setErrors(prev => ({ ...prev, [field]: "" }));

    if (field === "education") {
      if (["Below 10", "10 Pass and Above"].includes(val)) {
        setFormData(prev => ({ ...prev, education: val, specialization: "", completedYear: "" }));
        setSpecializationOptions([]);
      } else {
        setFormData(prev => ({ ...prev, education: val, specialization: "" }));
        await fetchSpecializationForEducation(val);
      }
    }
  }, []);

  const handleSave = useCallback(async () => {
    const fd = formDataRef.current;
    const _isLow = fd.education === "Below 10" || fd.education === "10 Pass and Above";
    setTouched({ education: true, ...(!_isLow ? { specialization: true, completedYear: true } : {}) });

    const isValid = validateRef.current(false);
    if (!isValid) return false;

    setLoading(true);
    try {
      const apiData = {
        qualification: fd.education,
        specialization: _isLow ? "" : fd.specialization,
        passed_out_year: _isLow ? "" : fd.completedYear
      };
      const response = await PostRegister(id, 4, apiData, recordType);
      if (response?.code === 200) {
        if (onSubmit) onSubmit(apiData, false);
        return true;
      } else {
        setErrors({ submit: response?.message || "Failed to save education data" });
        return false;
      }
    } catch (error) {
      console.error("Error saving form4:", error);
      setErrors({ submit: "Network error. Please try again." });
      return false;
    } finally { setLoading(false); }
  }, [id, recordType, onSubmit]);

  if (loading) {
    return (
      <div className={styles.formContainer}>
        <div className={styles.formContent}><div className={styles.formTitle}>Loading Education Data...</div></div>
      </div>
    );
  }

  return (
    <div className={styles.formContainer}>
      <div className={styles.formContent}>
        <div className={styles.formTitle}>Education Details</div>
        {errors.submit && <div className={styles.errorSummary}>{errors.submit}</div>}

        <div className={styles.formRow}>
          <div className={styles.formGroup} data-field="education">
            <label>Education <span className={styles.required}>*</span></label>
            <AutoSelect
              options={EDUCATION_OPTIONS}
              placeholder="Select your education"
              value={formData.education}
              onChange={handleSelect("education")}
              isError={!!errors.education}
            />
            {errors.education && !formData.education && <span className={styles.errorMsg}>{errors.education}</span>}
          </div>

          {!isLowEducation && formData.education && (
            <div className={styles.formGroup} data-field="specialization">
              <label>Specialization <span className={styles.required}>*</span></label>
              <AutoSelect
                options={specializationOptions}
                placeholder={loadingSpecialization ? "Loading specializations..." : "Select your specialization"}
                value={formData.specialization}
                onChange={handleSelect("specialization")}
                isError={!!errors.specialization}
                disabled={!formData.education}
                loading={loadingSpecialization}
              />
              {errors.specialization && !formData.specialization && <span className={styles.errorMsg}>{errors.specialization}</span>}
            </div>
          )}
        </div>

        {!isLowEducation && formData.education && (
          <div className={styles.formRow}>
            <div className={styles.formGroup} data-field="completedYear">
              <label>Degree Completed Year <span className={styles.required}>*</span></label>
              <AutoSelect
                options={YEAR_OPTIONS}
                placeholder="Select completion year"
                value={formData.completedYear}
                onChange={handleSelect("completedYear")}
                isError={!!errors.completedYear}
              />
              {errors.completedYear && !formData.completedYear && <span className={styles.errorMsg}>{errors.completedYear}</span>}
            </div>
          </div>
        )}

        {isLowEducation && formData.education && (
          <div className={styles.infoMessage}>
            <span className={styles.infoText}>No specialization or completion year required for {formData.education} level.</span>
          </div>
        )}
      </div>

      <div className={styles.buttonWrapper}>
        <div className={styles.buttonGroup}>
          <button className={styles.btnCancel} onClick={onBack}>Back</button>
          <button className={styles.btnSave} onClick={handleSave}>
            <img src={saveIcon} alt="" width="16" height="16" />
            Save & Continue
          </button>
        </div>
      </div>
    </div>
  );
}